import { Suspense, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';

// !! Very important to keep current order to prevent cycling imports
// eslint-disable-next-line import/order
import AsyncRouterKiosk from '@app/kiosk/src/routes/RouterKiosk';
import { BrowserUrlConsts } from '@lib/core/service/consts';
import ServiceLauncher from '@lib/core/service/providers/ServiceLauncher';
import { updateCurrentDateParams } from '@lib/core/service/slices/qaParameterSlice';
import { store } from '@lib/core/service/store';

// !! Very important to keep current order to prevent cycling imports
// eslint-disable-next-line import/order
import { KioskSessionManager } from '@app/kiosk/src/containers/KioskSessionManager';
import { Navigator } from '@lib/core/service/utils/Navigator';
import UserProvider from '@lib/core/users/providers/UserProvider';
import DatProvider from '@lib/tools/dat/views/DatProvider';
import { DevTools } from '@lib/tools/devtools';
import { LocaleProvider } from '@lib/tools/locale/providers/LocaleProvider';
import ScannerHOC from '@lib/tools/scanner/providers/ScannerProvider';
import { DataProvider } from '@lib/tools/shared/views/providers/DataProvider';
import { HocWrapperKiosk } from '@lib/tools/shared/views/providers/HocWrapperKiosk';
import { ThemeProvider } from '@lib/tools/shared/views/providers/ThemeProvider';
import { useTypedDispatch } from '@lib/tools/views/hooks';

import { GlobalStyle } from '@components/web/src/styles';
import { LoadingSpinner } from '@components/web/src/templates/Loading/LoadingSpinner/LoadingSpinner';

// Styles
import 'rc-slider/assets/index.css';
import '@components/web/src/styles/_index.scss';

const persistor = persistStore(store);

const View = () => {
  const { location } = window;

  const dispatch = useTypedDispatch();

  useEffect(() => {
    // INFO: This part is done for QA accessibility so that we can change the params part
    // to check different unique items per day passing the date as current date on params
    if (location.search) {
      const searchParams = new URLSearchParams(location.search);
      const qaCurrentDate = searchParams.get(BrowserUrlConsts.currentDateDay);

      if (qaCurrentDate) {
        dispatch(updateCurrentDateParams(qaCurrentDate));
      }
    }

    return () => {
      new AbortController().abort();
    };
  }, []);

  return (
    <LocaleProvider>
      <BrowserRouter>
        <Navigator />
        <DevTools />

        <Suspense fallback={<LoadingSpinner />}>
          <DatProvider>
            <UserProvider>
              <DataProvider>
                <ThemeProvider>
                  <HocWrapperKiosk>
                    <KioskSessionManager>
                      <ScannerHOC>
                        <AsyncRouterKiosk />
                      </ScannerHOC>
                    </KioskSessionManager>
                  </HocWrapperKiosk>
                </ThemeProvider>
              </DataProvider>
            </UserProvider>
          </DatProvider>
        </Suspense>
      </BrowserRouter>
    </LocaleProvider>
  );
};

const App = ServiceLauncher(View);

createRoot(document.getElementById('root')!).render(
  <PersistGate loading={null} persistor={persistor}>
    <ReduxProvider store={store}>
      <GlobalStyle />
      <App key="kiosk" props={undefined} type={undefined} />
    </ReduxProvider>
  </PersistGate>,
);
